import {
  AUTOMATION_ACTION_KEY,
  AUTOMATION_CONDITION_KEY,
  AutomationActionKey,
  AutomationActionValue,
  AutomationConditionKey,
  AutomationConditionValue,
} from "../../../../slices/AutomationSlice";
import { SelectOptionType } from "../../../../types/types";

export type AutomationScope = "work" | "subtask";

export type AutomationOptionsKey =
  | "workStatuses"
  | "subtaskStatuses"
  | "users"
  | "teamMembers"
  | "emailTemplates"
  | "dateRules";

export type SelectControl = {
  type: "select";
  placeholder: string;
  optionsKey: AutomationOptionsKey;
};

export type DateOffsetControl = {
  type: "dateOffset";
  placeholder: string;
  optionsKey: "dateRules";
};

export type EmptyControl = {
  type: "none";
};

export type AutomationControl =
  | SelectControl
  | DateOffsetControl
  | EmptyControl;

export type ConditionConfig = {
  key: AutomationConditionKey;
  scope: AutomationScope;
  label: string;
  control: AutomationControl;
  compatibleActions: AutomationActionKey[];
  renderSummary: (value?: string, extraValue?: string) => string;
};

export type ActionConfig = {
  key: AutomationActionKey;
  scopes: AutomationScope[];
  label: string;
  control: AutomationControl;
  renderSummary: (value?: string) => string;
};

export type AutomationItem = {
  id: string;
  scope: AutomationScope;

  workItemId?: string;
  workItemTitle?: string;

  subtaskId?: string;
  subtaskTitle?: string;

  conditionId: string;
  conditionKey?: AutomationConditionKey;
  conditionValue?: AutomationConditionValue;

  actionId: string;
  actionKey?: AutomationActionKey;
  actionValue?: AutomationActionValue;

  createdBy: string;
  /** Shown instead of "Created by …" for copies without a creator (QA batch 14, AUTO-016). */
  sourceText?: string;
  createdAt: string;
  lastRunAt?: string;
  lastRunStatus: "success" | "failed" | "idle";
};

export type AutomationDraft = {
  scope: AutomationScope;
  conditionId?: string;
  conditionKey?: AutomationConditionKey;
  conditionValue: string;
  conditionExtraValue: string;
  actionId?: string;
  actionKey?: AutomationActionKey;
  actionValue: string;
  actionExtraValue: string;
};

export type AutomationLookupOptions = Record<
  AutomationOptionsKey,
  SelectOptionType[]
>;

export const AUTOMATION_CONDITION_CONFIGS: ConditionConfig[] = [
  {
    key: AUTOMATION_CONDITION_KEY.WORK_STATUS,
    scope: "work",
    label: "the work has a status of",
    control: {
      type: "select",
      placeholder: "Select work status",
      optionsKey: "workStatuses",
    },
    compatibleActions: [
      AUTOMATION_ACTION_KEY.ASSIGNEE,
      AUTOMATION_ACTION_KEY.SEND_EMAIL,
      AUTOMATION_ACTION_KEY.SEND_NOTIFICATION,
    ],
    renderSummary: (value) => `has a status of ${value}`,
  },
  {
    key: AUTOMATION_CONDITION_KEY.REACHED_DUE_DATE,
    scope: "work",
    label: "the due date is reached",
    control: {
      type: "none",
    },
    compatibleActions: [
      AUTOMATION_ACTION_KEY.ASSIGNEE,
      AUTOMATION_ACTION_KEY.STATUS,
      AUTOMATION_ACTION_KEY.SEND_EMAIL,
      AUTOMATION_ACTION_KEY.SEND_NOTIFICATION,
    ],
    renderSummary: () => "due date is reached",
  },
  {
    key: AUTOMATION_CONDITION_KEY.WORK_REACHES_DATE,
    scope: "work",
    label: "the work reaches a date",
    control: {
      type: "dateOffset",
      placeholder: "Enter days",
      optionsKey: "dateRules",
    },
    compatibleActions: [
      AUTOMATION_ACTION_KEY.ASSIGNEE,
      AUTOMATION_ACTION_KEY.STATUS,
      AUTOMATION_ACTION_KEY.SEND_EMAIL,
      AUTOMATION_ACTION_KEY.SEND_NOTIFICATION,
    ],
    renderSummary: (value, extraValue) => `reaches ${value} ${extraValue}`,
  },
  {
    key: AUTOMATION_CONDITION_KEY.CURRENT_SUBTASK_STATUS,
    scope: "subtask",
    label: "current subtask has a status of",
    control: {
      type: "select",
      placeholder: "Select subtask status",
      optionsKey: "subtaskStatuses",
    },
    compatibleActions: [AUTOMATION_ACTION_KEY.SEND_NOTIFICATION],
    renderSummary: (value) => `has a status of ${value}`,
  },
  {
    key: AUTOMATION_CONDITION_KEY.PREVIOUS_SUBTASK_STATUS,
    scope: "subtask",
    label: "the previous subtask has a status of",
    control: {
      type: "select",
      placeholder: "Select subtask status",
      optionsKey: "subtaskStatuses",
    },
    compatibleActions: [AUTOMATION_ACTION_KEY.CHANGE_STATUS],
    renderSummary: (value) => `previous subtask has a status of ${value}`,
  },
  {
    key: AUTOMATION_CONDITION_KEY.ALL_SUBTASKS_COMPLETED,
    scope: "subtask",
    label: "all previous subtasks are completed",
    control: {
      type: "none",
    },
    compatibleActions: [
      AUTOMATION_ACTION_KEY.CHANGE_STATUS,
      AUTOMATION_ACTION_KEY.SEND_NOTIFICATION,
    ],
    renderSummary: () => "all previous subtasks are completed",
  },
  {
    key: AUTOMATION_CONDITION_KEY.SUBTASK_REACHED_DUE_DATE,
    scope: "subtask",
    label: "subtask has reached its due date",
    control: {
      type: "none",
    },
    compatibleActions: [
      AUTOMATION_ACTION_KEY.CHANGE_STATUS,
      AUTOMATION_ACTION_KEY.SEND_NOTIFICATION,
    ],
    renderSummary: () => "has reached its due date",
  },
];

export const AUTOMATION_ACTION_CONFIGS: ActionConfig[] = [
  {
    key: AUTOMATION_ACTION_KEY.ASSIGNEE,
    scopes: ["work", "subtask"],
    label: "add assignee(s) to this work item",
    control: {
      type: "select",
      placeholder: "Select assignee",
      optionsKey: "users",
    },
    renderSummary: (value) => `add ${value} as assignee`,
  },
  {
    key: AUTOMATION_ACTION_KEY.STATUS,
    scopes: ["work"],
    label: "change the status to",
    control: {
      type: "select",
      placeholder: "Select work status",
      optionsKey: "workStatuses",
    },
    renderSummary: (value) => `change the status to ${value}`,
  },
  {
    key: AUTOMATION_ACTION_KEY.SEND_EMAIL,
    scopes: ["work"],
    label: "send a client email",
    control: {
      type: "select",
      placeholder: "Select email template",
      optionsKey: "emailTemplates",
    },
    renderSummary: (value) => `send client email "${value}"`,
  },
  {
    key: AUTOMATION_ACTION_KEY.SEND_NOTIFICATION,
    scopes: ["work", "subtask"],
    label: "send a team member a notification",
    control: {
      type: "select",
      placeholder: "Select team member",
      optionsKey: "teamMembers",
    },
    renderSummary: (value) => `send ${value} a notification`,
  },
  {
    key: AUTOMATION_ACTION_KEY.CHANGE_STATUS,
    scopes: ["subtask"],
    label: "change the current subtask status to",
    control: {
      type: "select",
      placeholder: "Select subtask status",
      optionsKey: "subtaskStatuses",
    },
    renderSummary: (value) => `change the current subtask status to ${value}`,
  },
];

export const HIDE_AUTOMATION = false;
